import { z } from "zod";

const optional = z
  .string()
  .trim()
  .min(1)
  .nullish()
  .or(z.literal("").transform(() => null));

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ")
  .nullish()
  .or(z.literal("").transform(() => null));

export const saveProjectInput = z.object({
  projectId: z.string().uuid().optional(),
  confirmationId: z.string().optional(),

  clientId: z.string().uuid("Choisissez un client"),
  name: z.string().trim().min(2, "Le nom du chantier est trop court"),
  description: optional,

  siteAddress: optional,
  sitePostalCode: z
    .string()
    .regex(/^\d{5}$/, "Le code postal doit faire 5 chiffres")
    .nullish()
    .or(z.literal("").transform(() => null)),
  siteCity: optional,

  plannedStartDate: isoDate,
  plannedEndDate: isoDate,

  /** Enveloppe hors taxes. Facultative : tous les chantiers n'en ont pas. */
  budgetHt: z.coerce.number().min(0).nullish(),
  quoteId: z.string().uuid().nullish(),
});

export type SaveProjectInput = z.infer<typeof saveProjectInput>;

export const listProjectsInput = z.object({
  confirmationId: z.string().optional(),
  status: z
    .array(
      z.enum([
        "planned",
        "permit_pending",
        "in_progress",
        "paused",
        "stopped",
        "completed",
        "invoiced",
        "closed",
      ]),
    )
    .optional(),
  clientId: z.string().uuid().optional(),
});

export type ListProjectsInput = z.infer<typeof listProjectsInput>;

export const PROJECT_STATUS_VALUES = [
  "planned",
  "permit_pending",
  "in_progress",
  "paused",
  "stopped",
  "completed",
  "invoiced",
  "closed",
] as const;

/** Changer le statut d'un chantier, avec le motif d'une pause ou d'un arrêt. */
export const changeProjectStatusInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
  status: z.enum(PROJECT_STATUS_VALUES, { message: "Statut de chantier inconnu" }),
  reason: z
    .string()
    .trim()
    .max(300, "Motif trop long (300 caractères au plus)")
    .nullish()
    .transform((v) => (v ? v : null)),
});

export type ChangeProjectStatusInput = z.infer<typeof changeProjectStatusInput>;

export const readProjectInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid(),
});

export type ReadProjectInput = z.infer<typeof readProjectInput>;

/**
 * Une phase de chantier, validée.
 *
 * La table existe depuis la migration d'origine et n'a jamais eu de porte
 * d'écriture : ni service, ni méthode de dépôt, ni écran. Ce schéma est cette
 * porte. Voir la migration `20260301002600`.
 */
export const savePhaseInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  phaseId: z.string().uuid().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
  name: z
    .string()
    .trim()
    .min(1, "Un intitulé de phase est attendu")
    .max(120, "Intitulé trop long"),
  /**
   * L'ordre d'exécution. Il décide de l'affichage, et rien d'autre : aucune
   * dépendance n'est vérifiée entre phases. Un chantier de bâtiment se réordonne
   * en cours de route bien plus souvent qu'un diagramme ne le prévoit, et un
   * moteur de dépendances qui refuserait la vérité du terrain serait contourné
   * dès la première semaine.
   */
  sortOrder: z.coerce.number().int().min(0).max(999).default(0),
  progressPercent: z.coerce
    .number({ message: "Un pourcentage est attendu" })
    .int("Un avancement s'exprime en points entiers")
    .min(0, "Un avancement ne peut pas être négatif")
    .max(100, "Un avancement ne dépasse pas 100 %")
    .default(0),
  /**
   * `blocked` est le seul statut que l'avancement ne recouvre pas : une phase
   * bloquée à 60 % reste bloquée, et c'est l'information la plus utile de la
   * ligne. Les trois autres suivent l'avancement — voir `sync_phase_status`.
   */
  status: z.enum(["pending", "in_progress", "completed", "blocked"]).default("pending"),
  plannedStart: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ")
    .nullish()
    .or(z.literal("").transform(() => null))
    .transform((v) => v ?? null),
  plannedEnd: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ")
    .nullish()
    .or(z.literal("").transform(() => null))
    .transform((v) => v ?? null),
  /**
   * Le lot de la phase. Absent : on n'y touche pas — un appel qui ne parle
   * que de l'avancement ne sort pas la phase de son lot. `null` ou vide : la
   * phase n'a pas de lot.
   */
  lotId: z
    .string()
    .uuid("Identifiant de lot attendu")
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
});

export const removePhaseInput = z.object({
  confirmationId: z.string().optional(),
  phaseId: z.string().uuid("Identifiant de phase attendu"),
});

const isoDay = z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

/**
 * Un lot du chantier : un corps de métier. Voir `20261030230000`.
 */
export const saveLotInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  lotId: z.string().uuid().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
  name: z.string().trim().min(1, "Un nom de lot est attendu").max(120, "Nom trop long"),
  sortOrder: z.coerce.number().int().min(0).max(999).default(0),
  notes: z
    .string()
    .trim()
    .max(2000, "Note trop longue")
    .nullish()
    .transform((v) => (v ? v : null)),
  /**
   * Les phases à poser dans le lot, à sa création seulement : les phases
   * types du corps de métier, en un geste. Ignoré en mise à jour.
   */
  phases: z.array(z.string().trim().min(1).max(120)).max(30, "Trente phases au plus").default([]),
});

export const removeLotInput = z.object({
  confirmationId: z.string().optional(),
  lotId: z.string().uuid("Identifiant de lot attendu"),
});

/**
 * Les phases qu'une phase attend. La liste entière remplace l'ancienne ; vide,
 * la phase n'attend plus rien.
 */
export const setPhasePredecessorsInput = z.object({
  confirmationId: z.string().optional(),
  phaseId: z.string().uuid("Identifiant de phase attendu"),
  predecessors: z
    .array(
      z.object({
        dependsOnId: z.string().uuid("Identifiant de phase attendu"),
        lagDays: z.coerce
          .number({ message: "Un nombre de jours est attendu" })
          .int("Un délai se compte en jours entiers")
          .min(0, "Un délai ne peut pas être négatif")
          .max(365, "Un délai ne dépasse pas un an")
          .default(0),
      }),
    )
    .max(20, "Vingt enchaînements au plus"),
});

/** Redater plusieurs phases d'un chantier, d'un coup. */
export const shiftPhasesInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
  moves: z
    .array(
      z.object({
        phaseId: z.string().uuid("Identifiant de phase attendu"),
        plannedStart: isoDay,
        plannedEnd: isoDay.nullable(),
      }),
    )
    .min(1, "Aucune phase à décaler")
    .max(200),
});

export type SaveLotInput = z.infer<typeof saveLotInput>;
export type RemoveLotInput = z.infer<typeof removeLotInput>;
export type SetPhasePredecessorsInput = z.infer<typeof setPhasePredecessorsInput>;
export type ShiftPhasesInput = z.infer<typeof shiftPhasesInput>;

export type SavePhaseInput = z.infer<typeof savePhaseInput>;
export type SavePhaseFormInput = z.input<typeof savePhaseInput>;
export type RemovePhaseInput = z.infer<typeof removePhaseInput>;
